import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Typography,
  Container,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

type Phase = 'loading' | 'invalid' | 'password' | 'done';

// Mirror of the backend password policy (password_validator.py) for instant
// feedback. The backend remains the source of truth.
const PASSWORD_RULES = [
  { label: 'At least 10 characters', test: (p: string) => p.length >= 10 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  {
    label: 'One special character',
    test: (p: string) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`'"\\]/.test(p),
  },
];

const ResetPassword: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Validate the token from the URL on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = (params.get('token') || '').trim();
    setToken(t);

    if (!t) {
      setPhase('invalid');
      setError('This reset link is missing its token. Please use the link from your email.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/account/reset/validate?token=${encodeURIComponent(t)}`), {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.valid) {
          setPhase('invalid');
          setError(data.error || 'This reset link is invalid or has expired.');
          return;
        }
        setEmail(data.email || '');
        setFirstName(data.first_name || '');
        setPhase('password');
      } catch {
        setPhase('invalid');
        setError('Cannot reach the server. Please check your connection and try again.');
      }
    })();
  }, []);

  const allRulesPass = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPass) {
      setError('Password does not meet all the requirements below.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/account/reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || 'Could not reset your password. Please try again.');
        return;
      }

      // Password updated — the user signs in again normally (with MFA).
      setPhase('done');
    } catch {
      setError('Cannot reach the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
              mb: 2,
              boxShadow: '0 8px 32px rgba(47, 74, 225, 0.3)',
            }}
          >
            <LockIcon sx={{ fontSize: 44, color: 'white' }} />
          </Paper>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#1f2937', mb: 1 }}>
            Reset Your Password
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280' }}>
            Choose a new password for your account
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Loading */}
            {phase === 'loading' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: '#6b7280' }}>Validating your link…</Typography>
              </Box>
            )}

            {/* Invalid / expired */}
            {phase === 'invalid' && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CancelIcon sx={{ fontSize: 56, color: '#ef4444', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#1f2937', mb: 1 }}>
                  Link not valid
                </Typography>
                <Typography sx={{ color: '#6b7280', mb: 3 }}>
                  {error || 'This reset link is invalid or has expired.'}
                </Typography>
                <Button variant="outlined" onClick={() => window.location.replace('/')}>
                  Go to Login
                </Button>
              </Box>
            )}

            {/* Success */}
            {phase === 'done' && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: '#12b59f', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#1f2937', mb: 1 }}>
                  Password reset
                </Typography>
                <Typography sx={{ color: '#6b7280', mb: 3 }}>
                  Your password has been updated. Please sign in with your new password.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => window.location.replace('/')}
                  sx={{
                    py: 1.25,
                    px: 4,
                    background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            )}

            {/* Set new password */}
            {phase === 'password' && (
              <form onSubmit={handleReset}>
                <Stack spacing={2.5}>
                  <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center' }}>
                    {firstName ? `Hi ${firstName}, ` : ''}set a new password for{' '}
                    <strong>{email}</strong>
                  </Typography>

                  <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    error={confirmPassword.length > 0 && !passwordsMatch}
                    helperText={
                      confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '
                    }
                  />

                  {/* Live policy checklist */}
                  <Box sx={{ bgcolor: '#f9fafb', borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                      Password must contain:
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {PASSWORD_RULES.map((rule) => {
                        const ok = rule.test(password);
                        return (
                          <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {ok ? (
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#12b59f' }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />
                            )}
                            <Typography variant="caption" sx={{ color: ok ? '#12b59f' : '#9ca3af' }}>
                              {rule.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !allRulesPass || !passwordsMatch}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                  </Button>
                </Stack>
              </form>
            )}

            {/* Footer badge */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: 'rgba(47, 74, 225, 0.06)',
                  border: '1px solid rgba(47, 74, 225, 0.15)',
                }}
              >
                <SecurityIcon sx={{ fontSize: 14, color: '#2f4ae1' }} />
                <Typography variant="caption" sx={{ color: '#2f4ae1', fontWeight: 600, fontSize: '0.7rem' }}>
                  Secure password reset
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResetPassword;
